import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useAuthStore } from '../store/authStore';
import { useRegion } from '../hooks/useRegion';
import { Layout } from '../components/Layout';
import { buildJobPayload } from '../lib/jobPayload';
import { crewSuperannuationFor } from '../lib/jobFinancials';
import { JobForm } from '../components/job/JobForm';
import { UnsavedChangesDialog, useUnsavedChangesGuard } from '../components/UnsavedChangesGuard';
import { useTrackedEdits } from '../lib/useTrackedEdits';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { DEFAULT_PHASE_RATES } from '../lib/phaseRates';

export default function JobEditPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { fetchJob, updateJob, getBrief, equipment, fetchEquipment, fetchSettings, equipmentKits, fetchEquipmentKits, clients, fetchClients, crewMembers, fetchCrewMembers } = useAppStore();
  const { checkProjectsPlanAccess, projectsPlanChecked } = useAuthStore();
  const { region, regionConfig } = useRegion();
  const [loading, setLoading] = useState(false);
  const [linkedBrief, setLinkedBrief] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [defaultRates, setDefaultRates] = useState(DEFAULT_PHASE_RATES);
  
  // Package dialog state
  // Active drag item for DragOverlay
  
  const [formData, setFormData] = useState({
    client: '',
    client_id: '',
    contact_email: '',
    client_job_number: '',
    client_job_name: '',
    project_name: '',
    description: '',
    phases: [],
    crew_labour: [],
    crew_equipment: [],
    crew_superannuation: crewSuperannuationFor(region),
    equipment: [],
    equipment_packages: [],
    equipment_display_order: [], // Array of {type: 'package'|'equipment', id: string}
    expenses: [],
    hidden_costs: [],
    include_gst: false,
    gst_rate: regionConfig.tax.defaultRate,
    equipment_rental_only: false,
    rental_start_date: '',
    rental_end_date: '',
  });

  // Loads write with setFormData; everything the user does goes through
  // setTracked, so only their edits count as unsaved.
  const { dirty, setTracked } = useTrackedEdits(formData, setFormData);
  const { blocker, allowNavigation } = useUnsavedChangesGuard(dirty);


  // Check Projects plan access on mount
  useEffect(() => {
    if (!projectsPlanChecked) {
      checkProjectsPlanAccess();
    }
  }, [projectsPlanChecked, checkProjectsPlanAccess]);

  useEffect(() => {
    const loadData = async () => {
      await fetchEquipment();
      await fetchEquipmentKits();
      await fetchClients();
      await fetchCrewMembers();
      const settingsData = await fetchSettings();
      if (settingsData?.default_phase_rates) {
        setDefaultRates({ ...DEFAULT_PHASE_RATES, ...settingsData.default_phase_rates });
      }
      
      const job = await fetchJob(jobId);
      if (job) {
        // Add IDs to items for drag-drop
        const phasesWithIds = (job.phases || []).map(p => ({ ...p, id: p.id || crypto.randomUUID() }));
        const crewLabourWithIds = (job.crew_labour || []).map(c => ({ ...c, id: c.id || crypto.randomUUID() }));
        const crewEquipmentWithIds = (job.crew_equipment || []).map(e => ({ ...e, id: e.id || crypto.randomUUID() }));
        const equipmentWithIds = (job.equipment || []).map(e => ({ ...e, id: e.id || crypto.randomUUID() }));
        const equipmentPackagesWithIds = (job.equipment_packages || []).map(p => ({ ...p, id: p.id || crypto.randomUUID() }));
        const expensesWithIds = (job.expenses || []).map(e => ({ ...e, id: e.id || crypto.randomUUID() }));
        const hiddenCostsWithIds = (job.hidden_costs || []).map(e => ({ ...e, id: e.id || crypto.randomUUID() }));
        
        // Build equipment_display_order - try to restore saved order, or build fresh
        let displayOrder = [];
        const packageEquipmentIds = new Set(equipmentPackagesWithIds.flatMap(pkg => pkg.equipment_ids || []));
        
        // Check if we have a saved display order to restore
        if (Array.isArray(job.equipment_display_order) && job.equipment_display_order.length > 0) {
          // Track which packages have been matched to avoid duplicates
          const matchedPackageIds = new Set();
          
          // Restore order using saved references
          displayOrder = job.equipment_display_order
            .map(item => {
              if (item.type === 'divider') {
                return { type: 'divider', id: item.divider_id || item.id || crypto.randomUUID(), label: item.label || '' };
              }
              if (item.type === 'crew_equipment') {
                const ce = crewEquipmentWithIds.find(c => c.id === item.crew_equipment_id);
                return ce ? { type: 'crew_equipment', id: ce.id } : null;
              }
              if (item.type === 'package') {
                // Try to match by equipment_ids_key first (unique), then fall back to name
                let pkg = null;
                if (item.equipment_ids_key) {
                  pkg = equipmentPackagesWithIds.find(p => {
                    const pkgKey = p.equipment_ids ? [...p.equipment_ids].sort().join(',') : '';
                    return pkgKey === item.equipment_ids_key && !matchedPackageIds.has(p.id);
                  });
                }
                // Fall back to name matching for legacy data (but avoid duplicates)
                if (!pkg && item.name) {
                  pkg = equipmentPackagesWithIds.find(p => p.name === item.name && !matchedPackageIds.has(p.id));
                }
                if (pkg) {
                  matchedPackageIds.add(pkg.id);
                  return { type: 'package', id: pkg.id };
                }
                return null;
              } else {
                const eq = equipmentWithIds.find(e => e.inventory_id === item.inventory_id);
                // Only include if not in a package
                if (eq && !packageEquipmentIds.has(eq.inventory_id)) {
                  return { type: 'equipment', id: eq.id };
                }
                return null;
              }
            })
            .filter(Boolean);
          
          // Add any new packages/equipment that weren't in the saved order
          equipmentPackagesWithIds.forEach(pkg => {
            if (!displayOrder.find(o => o.type === 'package' && o.id === pkg.id)) {
              displayOrder.push({ type: 'package', id: pkg.id });
            }
          });
          equipmentWithIds.forEach(eq => {
            if (!packageEquipmentIds.has(eq.inventory_id) && !displayOrder.find(o => o.type === 'equipment' && o.id === eq.id)) {
              displayOrder.push({ type: 'equipment', id: eq.id });
            }
          });
          crewEquipmentWithIds.forEach(ce => {
            if (!displayOrder.find(o => o.type === 'crew_equipment' && o.id === ce.id)) {
              displayOrder.push({ type: 'crew_equipment', id: ce.id });
            }
          });
        } else if (equipmentWithIds.length > 0 || equipmentPackagesWithIds.length > 0 || crewEquipmentWithIds.length > 0) {
          // No saved order - build fresh: packages first, then equipment not in packages, then crew equipment
          displayOrder = [
            ...equipmentPackagesWithIds.map(pkg => ({ type: 'package', id: pkg.id })),
            ...equipmentWithIds.filter(e => !packageEquipmentIds.has(e.inventory_id)).map(e => ({ type: 'equipment', id: e.id })),
            ...crewEquipmentWithIds.map(ce => ({ type: 'crew_equipment', id: ce.id })),
          ];
        }
        
        setFormData({
          client: job.client || '',
          client_id: job.client_id || '',
          contact_email: job.contact_email || '',
          client_job_number: job.client_job_number || '',
          client_job_name: job.client_job_name || '',
          project_name: job.project_name || '',
          description: job.description || '',
          phases: phasesWithIds,
          crew_labour: crewLabourWithIds,
          crew_equipment: crewEquipmentWithIds,
          crew_superannuation: crewSuperannuationFor(settingsData?.region || region, job.crew_superannuation),
          equipment: equipmentWithIds,
          equipment_packages: equipmentPackagesWithIds,
          equipment_display_order: displayOrder,
          expenses: expensesWithIds,
          hidden_costs: hiddenCostsWithIds,
          include_gst: job.include_gst || false,
          gst_rate: job.gst_rate || regionConfig.tax.defaultRate,
          equipment_rental_only: job.equipment_rental_only || false,
          rental_start_date: job.rental_start_date || '',
          rental_end_date: job.rental_end_date || '',
        });
        if (job.brief_id) {
          getBrief(job.brief_id).then(setLinkedBrief).catch(() => {});
        }
      }
      setInitialLoading(false);
    };
    loadData();
  }, [jobId, fetchJob, fetchEquipment, fetchSettings, fetchEquipmentKits]);

  // Phase handlers
  // Crew Labour handlers
  // Crew Equipment handlers
  // Equipment handlers
  // Track kit selection key to force re-render of Select

  // Add all equipment items from a kit
  // Drag start handler for equipment/packages
  // Combined drag handler for packages and equipment in one list
  // Divider line handlers
  // Package handlers
  // Package maths lives in lib/jobFinancials - see calculatePackageTotal there.
  // Expense handlers
  // Hidden Cost handlers
  // Calculate totals
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateJob(jobId, buildJobPayload(formData));
      toast.success('Job updated successfully');
      allowNavigation();
      navigate(`/jobs/${jobId}`);
    } catch (error) {
      toast.error('Failed to update job');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <JobForm
        mode="edit"
        formData={formData}
        setFormData={setTracked}
        clients={clients}
        equipment={equipment}
        equipmentKits={equipmentKits}
        crewMembers={crewMembers}
        defaultRates={defaultRates}
        loading={loading}
        onSubmit={handleSubmit}
        onBack={() => navigate(`/jobs/${jobId}`)}
        onCancel={() => navigate(`/jobs/${jobId}`)}
        linkedBrief={linkedBrief}
      />
      <UnsavedChangesDialog blocker={blocker} />
    </Layout>
  );
}
